import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { VerificationSession, VerificationUnit } from '../../../types/verification';
import * as verificationService from '../../../services/verificationService';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';
import { unitSchema, type UnitFormValues } from '@/features/verification/schemas/unit.schema';

interface UseUnitFormArgs {
  selectedSession: VerificationSession | null;
  onSaved: (sessionId: string) => void;
  onNotify: (message: string) => void;
}

const EMPTY: UnitFormValues = { unitNumber: '', unitName: '' };

/**
 * Unit add/edit controller. RHF + Zod behind the same public surface the page
 * already consumes, so VerificationPage and UnitDialog are unchanged apart from
 * receiving the form object.
 *
 * `isDirty` comes from RHF rather than a hand-kept baseline. RHF compares
 * against `defaultValues`, so editing a field back to its original value
 * reports clean again — matching the old baseline comparison exactly, which the
 * characterization test pins.
 */
export function useUnitForm({ selectedSession, onSaved, onNotify }: UseUnitFormArgs) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<VerificationUnit | null>(null);

  const form = useForm<UnitFormValues>({
    resolver: zodResolver(unitSchema),
    defaultValues: EMPTY,
  });

  const startAdd = useCallback(() => {
    setEditingUnit(null);
    form.reset(EMPTY);
    setOpen(true);
  }, [form]);

  const startEdit = useCallback((unit: VerificationUnit) => {
    setEditingUnit(unit);
    // reset() re-baselines dirty tracking; setValue would leave the form dirty
    // the moment it opens and fire the discard prompt on an untouched dialog.
    form.reset({ unitNumber: unit.unitNumber, unitName: unit.unitName });
    setOpen(true);
  }, [form]);

  const submit = useCallback(async (values: UnitFormValues) => {
    if (!selectedSession) return;
    try {
      if (editingUnit) {
        await verificationService.updateUnit(selectedSession.id, editingUnit.id, {
          unitNumber: values.unitNumber,
          unitName: values.unitName,
          sortOrder: editingUnit.sortOrder,
        });
      } else {
        await verificationService.addUnit(selectedSession.id, {
          unitNumber: values.unitNumber,
          unitName: values.unitName,
          sortOrder: selectedSession.units.length,
        });
      }
      setOpen(false);
      onSaved(selectedSession.id);
      onNotify(editingUnit ? t('success.updated') : t('verification.unitAdded'));
    } catch (err) {
      // The dialog stays open so the operator does not lose the entry.
      onNotify(resolveApiErrorMessage(t, err, { fallbackKey: 'verification.saveUnitFailed' }));
    }
  }, [selectedSession, editingUnit, onSaved, onNotify, t]);

  // Read during render: formState is a Proxy that only tracks a flag once it has
  // been read at render scope. Reading isDirty inside the callback below would
  // freeze it and silently disable the discard prompt — with no error and no
  // failing test, unless a test asserts on the prompt itself.
  const { isDirty } = form.formState;

  return {
    form,
    open,
    close: () => setOpen(false),
    isDirty,
    isEditing: !!editingUnit,
    startAdd,
    startEdit,
    save: form.handleSubmit(submit),
  };
}
